import React, { useState } from 'react';
import { Modal, Button, Row, Col, Input, message } from 'antd';
import {
    LoadingOutlined,
    CopyOutlined,
    CheckOutlined
} from '@ant-design/icons';
import { copyToClipboard } from '../../Utilities';
import axios from 'axios';
import ENVIRONMENT from '../../../../environments';
import './ShareModal.scss';
import './AIEditorModalViewer.scss';

const ShareModal = ({ isOpen, onClose, imageData }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    
    // Generate public link - you may need to adjust this based on your URL structure
    const getPublicLink = () => {
        const imageUrl = imageData?.selectedImageUrl || imageData?.uri;
        if (!imageUrl) return '';
        
        // Check if URL already starts with cloudfront default URL
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
            return imageUrl;
        }
        
        // If not, prepend the base URL from cloudfront
        const baseUrl = ENVIRONMENT.getBaseURL('aws');
        return baseUrl + imageUrl;
    };

    const publicLink = getPublicLink();

    const handleCopyLink = (show_message = true) => {
        if (publicLink) {
            copyToClipboard(publicLink, 'Link copied to clipboard!', show_message);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } else {
            message.error('No link available to copy');
        }
    };

    const handleSocialShare = (platform) => {
        if (!publicLink) {
            message.warning('No link available to share');
            return;
        }

        const url = encodeURIComponent(publicLink);
        const title = encodeURIComponent(imageData?.display_name || 'Check out this AI creation');
        const description = encodeURIComponent('Created with ALL3D AI');

        let shareUrl = '';

        switch (platform) {
            case 'facebook':
                shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
                break;
            case 'linkedin':
                shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
                break;
            case 'discord':
                // Discord doesn't have a direct share URL, so we copy the link
                handleCopyLink(false);
                message.info('Link copied! Paste it in your Discord channel.');
                return;
            case 'instagram':
                // Instagram doesn't support direct web sharing
                // Copy link so user can use it manually
                handleCopyLink(false);
                message.info('Link copied! You can share this on Instagram by pasting the link.');
                return;
            default:
                return;
        }

        if (shareUrl) {
            window.open(shareUrl, '_blank', 'width=600,height=400');
        }
    };

    const handleShareToLibrary = async () => {
        const renderId = imageData?.render_id || imageData?.id;
        const assetType = imageData?.image_type;
        if (!renderId) {
            message.error('Unable to share: missing image ID');
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                action: 'submit_image_to_public_library',
                render_id: renderId,
                asset_type: imageData?.image_type
            };
            console.log("*****", payload)
            await axios.post(ENVIRONMENT.AI_MODEL_HELPER, payload);
            
            message.success('Your image is submitted. Thanks for sharing!');
            setIsLoading(false);
            onClose();
        } catch (error) {
            console.error('Error submitting image to public library:', error);
            setIsLoading(false);
            message.error('Failed to share to library. Please try again.');
        }
    };

    return (
        <Modal
            className='share-modal'
            visible={isOpen}
            onCancel={onClose}
            title={<span className="manrope f-20 lh-28 w-500 black-085 justify-in-start">
                Share Your Creation
            </span>}
            closable={true}
            footer={null}
            centered={true}>
            <Row>
                <Col span={24} className='direction-flex col j-start-a-start'>
                    {/* Social Media Sharing Section */}
                    <div className='share-section'>
                        <div className='manrope f-14 w-700 lh-22 black-085 mb-16'>Share on Social Media</div>
                        <div className='social-platforms-grid'>
                            <div 
                                className={`social-platform-item ${!publicLink ? 'disabled' : ''}`}
                                onClick={() => handleSocialShare('facebook')}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && publicLink) { handleSocialShare('facebook'); } }}
                            >
                                <div className='social-icon-wrapper facebook'>
                                    <img src='/img/footer/facebook.svg' alt='Facebook' className='social-icon' />
                                </div>
                                <span className='manrope f-14 w-400 lh-22 black-085'>Facebook</span>
                            </div>

                            <div 
                                className={`social-platform-item ${!publicLink ? 'disabled' : ''}`}
                                onClick={() => handleSocialShare('linkedin')}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && publicLink) { handleSocialShare('linkedin'); } }}
                            >
                                <div className='social-icon-wrapper linkedin'>
                                    <img src='/img/footer/linkedin.svg' alt='LinkedIn' className='social-icon' />
                                </div>
                                <span className='manrope f-14 w-400 lh-22 black-085'>LinkedIn</span>
                            </div>

                            <div 
                                className={`social-platform-item ${!publicLink ? 'disabled' : ''}`}
                                onClick={() => handleSocialShare('instagram')}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && publicLink) { handleSocialShare('instagram'); } }}
                            >
                                <div className='social-icon-wrapper instagram'>
                                    <img src='/img/footer/instagram.svg' alt='Instagram' className='social-icon' />
                                </div>
                                <span className='manrope f-14 w-400 lh-22 black-085'>Instagram</span>
                            </div>

                            <div 
                                className={`social-platform-item ${!publicLink ? 'disabled' : ''}`}
                                onClick={() => handleSocialShare('discord')}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && publicLink) { handleSocialShare('discord'); } }}
                            >
                                <div className='social-icon-wrapper discord'>
                                    <svg className='social-icon' viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                                    </svg>
                                </div>
                                <span className='manrope f-14 w-400 lh-22 black-085'>Discord</span>
                            </div>
                        </div>
                    </div>

                    {/* Copy Public Link Section */}
                    <div className='share-section' style={{ marginTop: '24px' }}>
                        <div className='manrope f-14 w-700 lh-22 black-085 mb-8'>Public Link</div>
                        <div className='copy-link-container'>
                            <Input
                                className='copy-link-input manrope f-14'
                                value={publicLink}
                                readOnly
                                placeholder="No link available"
                            />
                            <Button
                                className={`copy-link-btn manrope f-14 w-500 lh-22 ${copied ? 'copied' : ''}`}
                                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                                onClick={handleCopyLink}
                                disabled={!publicLink}
                            >
                                {copied ? 'Copied!' : 'Copy Link'}
                            </Button>
                        </div>
                    </div>

                    {/* Share to ALL3D Library Section */}
                    <div className='share-section' style={{ marginTop: '24px' }}>
                        <div className='manrope f-14 w-700 lh-22 black-085 mb-8'>ALL3D Gallery</div>
                        <div className='manrope f-12 w-400 lh-18 gray-045 mb-16'>
                            Share your creation to the ALL3D public library for others to discover and use, and allow ALL3D to feature it on our social media channels.
                        </div>
                        <Button
                            className='manrope f-14 w-500 lh-22 ai-action-btn-blue pd-small'
                            onClick={handleShareToLibrary}
                            disabled={isLoading}
                            block
                        >
                            {isLoading && <LoadingOutlined className='mr-8' />}
                            Share to ALL3D Gallery
                        </Button>
                    </div>
                </Col>
            </Row>
        </Modal>
    );
};

export default ShareModal;
